'use client';

import { ArrowUpRight, Check, MessageSquare } from 'lucide-react';

import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import { SubpageHero } from '@/components/sections/subpage-hero';
import Testimonials from '@/components/sections/testimonials';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/lib/i18n/context';

/* -------------------------------------------------------------------------- */
/*  Locale-keyed content                                                       */
/* -------------------------------------------------------------------------- */

const CONTENT = {
  en: {
    heroTitle: 'Like Claude, for your product',
    heroSubtitle:
      'Companion is an AI chat layer you drop into your product. Your users control your entire platform by chatting — with dynamic UIs that build themselves around the task. We built it to run Open, and now you can put it in your product too.',
    whatEyebrow: 'Companion as a service',
    whatTitle:
      'Give your users a Claude-level chat that runs your platform for them.',
    whatBody:
      'Most products bury their power in menus and settings most users never find. Companion sits on top of yours and lets people just ask — it understands your platform, takes the action, and builds the right interface inside the chat to get it done.',
    features: [
      {
        emoji: '🎛️',
        title: 'Full control by chat',
        description:
          'Your users run your entire platform from one conversation. They ask for what they want instead of hunting through menus and settings.',
      },
      {
        emoji: '🧩',
        title: 'Dynamic UIs',
        description:
          'Companion renders the right interface inside the chat for the task at hand — forms, tables, controls — not walls of text.',
      },
      {
        emoji: '⚡',
        title: 'It acts, not just answers',
        description:
          'Companion takes real actions inside your product. It plans first, shows the plan, then executes once the user approves.',
      },
      {
        emoji: '🔌',
        title: 'Embeds in your product',
        description:
          'Connect your APIs and tools and ship a companion that knows your platform. It lives inside your app, on your domain.',
      },
      {
        emoji: '⏪',
        title: 'Audit & rollback',
        description:
          'Every action shows up as a card in the conversation, with a full audit trail. Anything it does can be reviewed and reversed.',
      },
      {
        emoji: '🐶',
        title: 'We dogfood it',
        description:
          'We run Open on Companion ourselves, every day. Every change ships to our public changelog — we sell what we actually use.',
      },
    ],
    dogfoodEyebrow: 'We run on it',
    dogfoodTitle:
      "We don't just sell Companion. We run our whole company on it.",
    dogfoodBody:
      'Our own team operates Open through Companion every single day — diagnosing, building, and shipping from the same chat your users would. Every change we make is public.',
    dogfoodCta: 'See every change in the changelog',
    capEyebrow: 'Complete capability set',
    capTitle: 'Everything Companion brings to your product.',
    capBody:
      'A chat-driven control layer for your platform — with dynamic UIs, real actions, and the safety controls to ship it with confidence.',
    capGroups: [
      {
        title: 'For your users',
        items: [
          'Run the whole platform by chat',
          'No menu-hunting or training',
          'Natural, multi-lingual conversation',
          'Works on your domain, in your app',
        ],
      },
      {
        title: 'Dynamic UI',
        items: [
          'Renders the right UI per task',
          'Inline forms, tables & controls',
          'Change cards in the conversation',
          'Tabbed cards for multi-step work',
        ],
      },
      {
        title: 'Actions & control',
        items: [
          'Takes real actions in your product',
          'Plan-first, approve before run',
          'Connects your APIs and tools',
          'Claude-level reasoning (Agent 5)',
        ],
      },
      {
        title: 'Trust, safety & proof',
        items: [
          'Full audit log of every action',
          'One-click rollback on anything',
          'You approve, edit, or stop any plan',
          'Dogfooded by us — see the changelog',
        ],
      },
    ],
    faqTitle: 'Frequently asked questions',
    faqSubtitle:
      'Everything teams ask before adding Companion to their product.',
    faq: [
      {
        q: 'What is Companion?',
        a: 'Companion is an AI chat layer you embed in your own product — like Claude, but scoped to your platform. It knows your product, takes real actions inside it, and builds dynamic UIs in the chat so your users can run everything by talking instead of clicking around.',
      },
      {
        q: 'What do you mean by “dynamic UIs”?',
        a: 'Instead of replying with plain text, Companion renders the right interface for the task right inside the chat — a form to fill, a table to scan, a control to flip, a change card to approve. The UI assembles itself around what the user is trying to do.',
      },
      {
        q: 'Can it actually change things in our platform without asking?',
        a: 'No. Every multi-step action is shown as a plan up front — the user approves, edits, or stops before anything runs. Every action lands as a card with a full audit trail, and anything Companion does can be reversed.',
      },
      {
        q: 'How does Companion connect to our product?',
        a: 'You connect your APIs and tools, and Companion learns to operate them. It embeds inside your app, on your domain, so it feels like a native part of your product rather than a bolted-on chatbot.',
      },
      {
        q: 'Do you use Companion yourselves?',
        a: 'Yes. We run Open on Companion every day — it’s how our own team operates the platform. Every change we ship shows up in our public changelog at docs.open.cx/changelogs.',
      },
      {
        q: 'How is this different from a generic AI chatbot?',
        a: 'A generic chatbot can talk about your product. Companion operates it — with direct access to your tools, plan-first execution, dynamic UIs, and a full audit-and-rollback loop. It does the work, it doesn’t just describe it.',
      },
    ],
    faqCta: 'Read the Companion changelog',
  },
  ar: {
    heroTitle: 'مثل Claude، بس لمنتجك',
    heroSubtitle:
      'Companion طبقة محادثة بالذكاء الاصطناعي تركّبها داخل منتجك. مستخدموك يتحكّمون بمنصتك كاملة من المحادثة، مع واجهات تتشكّل حول المهمة نفسها. بنيناه لتشغيل Open، وهو الآن جاهز لمنتجك أنت أيضاً.',
    whatEyebrow: 'Companion كخدمة',
    whatTitle: 'امنح مستخدميك محادثة بمستوى Claude تدير منصتك نيابةً عنهم.',
    whatBody:
      'أغلب المنتجات تدفن قدراتها في قوائم وإعدادات ما يصل إليها المستخدم أبداً. Companion يشتغل فوق منصتك ويترك للمستخدم يطلب اللي يريد بس: يفهم منصتك، وينفّذ الإجراء، ويبني الواجهة المناسبة داخل المحادثة حتى تكتمل المهمة.',
    features: [
      {
        emoji: '🎛️',
        title: 'تحكّم كامل بالمحادثة',
        description:
          'مستخدموك يشغّلون منصتك كاملة من محادثة وحدة. يطلبون اللي يحتاجونه بدل البحث في القوائم والإعدادات.',
      },
      {
        emoji: '🧩',
        title: 'واجهات ديناميكية',
        description:
          'Companion يعرض الواجهة المناسبة داخل المحادثة حسب المهمة — نماذج وجداول وأدوات تحكّم — لا جدران نصوص.',
      },
      {
        emoji: '⚡',
        title: 'ينفّذ، مو بس يرد',
        description:
          'Companion ينفّذ إجراءات حقيقية داخل منتجك. يخطّط أولاً، ويعرض الخطة، ثم ينفّذ بعد موافقة المستخدم.',
      },
      {
        emoji: '🔌',
        title: 'يندمج بمنتجك',
        description:
          'اربط واجهات API وأدواتك، وأطلق Companion يعرف منصتك من الداخل. يشتغل داخل تطبيقك، على نطاقك أنت.',
      },
      {
        emoji: '⏪',
        title: 'تدقيق وتراجع',
        description:
          'كل إجراء يظهر كبطاقة في المحادثة، مع سجل تدقيق كامل. وكل اللي ينفّذه قابل للمراجعة والتراجع.',
      },
      {
        emoji: '🐶',
        title: 'نستخدمه بأنفسنا',
        description:
          'نشغّل Open على Companion كل يوم. وكل تغيير يصدر في سجل التغييرات العلني — نبيع اللي نستخدمه فعلاً.',
      },
    ],
    dogfoodEyebrow: 'نعمل عليه',
    dogfoodTitle: 'ما نبيع Companion بس. نشغّل شركتنا كاملة عليه.',
    dogfoodBody:
      'فريقنا يشغّل Open من داخل Companion كل يوم — يشخّص ويبني ويطلق، من نفس المحادثة اللي يستخدمها مستخدموك. وكل تغيير نجريه علني.',
    dogfoodCta: 'شوف كل تغيير في سجل التغييرات',
    capEyebrow: 'القدرات كاملة',
    capTitle: 'كل اللي يضيفه Companion لمنتجك.',
    capBody:
      'طبقة تحكّم بالمحادثة لمنصتك — واجهات ديناميكية، وإجراءات حقيقية، وضوابط أمان تتيح لك إطلاقه بثقة.',
    capGroups: [
      {
        title: 'لمستخدميك',
        items: [
          'تشغيل المنصة كاملة بالمحادثة',
          'بلا بحث في القوائم ولا تدريب',
          'محادثة طبيعية بعدة لغات',
          'يشتغل على نطاقك، داخل تطبيقك',
        ],
      },
      {
        title: 'واجهة ديناميكية',
        items: [
          'يعرض الواجهة المناسبة لكل مهمة',
          'نماذج وجداول وأدوات تحكّم مدمجة',
          'بطاقات تغيير في المحادثة',
          'بطاقات بتبويبات للمهام متعددة الخطوات',
        ],
      },
      {
        title: 'الإجراءات والتحكم',
        items: [
          'ينفّذ إجراءات حقيقية في منتجك',
          'الخطة أولاً، وموافقتك قبل التنفيذ',
          'يربط واجهات API وأدواتك',
          'تفكير بمستوى Claude (Agent 5)',
        ],
      },
      {
        title: 'الثقة والأمان والإثبات',
        items: [
          'سجل تدقيق كامل لكل إجراء',
          'تراجع بضغطة وحدة عن أي إجراء',
          'توافق أو تعدّل أو توقف أي خطة',
          'نستخدمه بأنفسنا — اطّلع على سجل التغييرات',
        ],
      },
    ],
    faqTitle: 'الأسئلة الشائعة',
    faqSubtitle: 'كل اللي تسأله الفرق قبل إضافة Companion لمنتجها.',
    faq: [
      {
        q: 'وش هو Companion؟',
        a: 'Companion طبقة محادثة بالذكاء الاصطناعي تدمجها في منتجك — مثل Claude، بس محصورة على منصتك. يعرف منتجك، وينفّذ إجراءات حقيقية داخله، ويبني واجهات ديناميكية في المحادثة عشان يشغّل مستخدموك كل شيء بالكلام بدل التنقّل بين الشاشات.',
      },
      {
        q: 'وش تقصد بـ “الواجهات الديناميكية”؟',
        a: 'بدل الرد بنص عادي، يعرض Companion الواجهة المناسبة للمهمة مباشرة داخل المحادثة — نموذج تملؤه، جدول تقرأه، أداة تحكّم تبدّلها، بطاقة تغيير توافق عليها. الواجهة تتشكّل حول اللي يحاول المستخدم إنجازه.',
      },
      {
        q: 'هل يقدر فعلاً يغيّر أشياء في منصتنا بدون إذن؟',
        a: 'لا. كل إجراء متعدد الخطوات يظهر كخطة أولاً — والمستخدم يوافق أو يعدّل أو يوقف قبل تنفيذ أي شيء. وكل إجراء يترك بطاقة في المحادثة مع سجل تدقيق كامل، وكل اللي ينفّذه Companion قابل للتراجع.',
      },
      {
        q: 'كيف يندمج Companion بمنتجنا؟',
        a: 'تربط واجهات API وأدواتك، ويتعلّم Companion تشغيلها. يندمج داخل تطبيقك، على نطاقك، فيبدو للمستخدم جزءاً أصيلاً من منتجك — لا روبوت محادثة مُضاف من الخارج.',
      },
      {
        q: 'تستخدمون Companion بأنفسكم؟',
        a: 'نعم. نشغّل Open على Companion كل يوم — هكذا يدير فريقنا المنصة. وكل تغيير نطلقه يظهر في سجل التغييرات العلني على docs.open.cx/changelogs.',
      },
      {
        q: 'وش الفرق بينه وبين أي روبوت محادثة بالذكاء الاصطناعي؟',
        a: 'روبوت المحادثة العادي يتكلّم عن منتجك. Companion يشغّله — بوصول مباشر لأدواتك، وتنفيذ يبدأ بالخطة، وواجهات ديناميكية، ودورة تدقيق وتراجع كاملة. ينجز العمل، مو بس يصفه.',
      },
    ],
    faqCta: 'اقرأ سجل تغييرات Companion',
  },
} as const;

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function CompanionPage() {
  const { locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <>
      <SubpageHero
        eyebrow="Companion"
        title={<>{c.heroTitle}</>}
        subtitle={
          <span className="text-base md:text-lg">{c.heroSubtitle}</span>
        }
        worksWith={null}
      />

      {/* What is Companion as a service */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-4 text-xs font-medium tracking-widest text-neutral-500 uppercase">
              {c.whatEyebrow}
            </p>
            <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
              {c.whatTitle}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-neutral-500 md:text-lg">
              {c.whatBody}
            </p>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="bg-white pb-24 md:pb-32">
        <div className="container">
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-neutral-200 sm:grid-cols-2 lg:grid-cols-3">
            {c.features.map((feature) => (
              <div
                key={feature.title}
                className="flex flex-col gap-2 bg-white p-7 md:p-9"
              >
                <span className="text-2xl">{feature.emoji}</span>
                <h3 className="mt-2 text-lg text-neutral-900 md:text-xl">
                  {feature.title}
                </h3>
                <p className="text-sm leading-relaxed text-neutral-500 md:text-[15px]">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dogfooding / credibility band */}
      <section className="bg-neutral-950 py-24 md:py-32">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 text-neutral-400">
              <MessageSquare className="h-4 w-4" />
              <span className="text-[11px] font-medium uppercase tracking-widest">
                {c.dogfoodEyebrow}
              </span>
            </div>
            <h2 className="font-serif text-2xl text-balance text-white md:text-4xl">
              {c.dogfoodTitle}
            </h2>
            <p className="mt-5 text-base leading-relaxed text-neutral-400 md:text-lg">
              {c.dogfoodBody}
            </p>
            <div className="mt-8 flex justify-center">
              <Button size="lg" asChild>
                <a
                  href="https://docs.open.cx/changelogs"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {c.dogfoodCta}
                  <ArrowUpRight className="size-4 ltr:ml-2 rtl:mr-2" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Complete capability checklist */}
      <section className="bg-white py-16 md:py-32">
        <div className="container">
          <div className="mb-14 max-w-2xl md:mb-20">
            <p className="mb-4 text-xs font-medium tracking-widest text-neutral-500 uppercase">
              {c.capEyebrow}
            </p>
            <h2 className="font-serif text-2xl text-balance text-neutral-900 md:text-3xl">
              {c.capTitle}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-500 md:text-lg">
              {c.capBody}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {c.capGroups.map((group) => (
              <div key={group.title}>
                <h3 className="mb-5 text-sm font-medium tracking-wide text-neutral-900 uppercase">
                  {group.title}
                </h3>
                <ul className="space-y-3">
                  {group.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-sm leading-snug text-neutral-600 md:text-[15px]"
                    >
                      <Check
                        className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500"
                        strokeWidth={2.5}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer testimonial videos — same component as the home page */}
      <CustomerVideos />

      {/* FAQ */}
      <FaqSection
        title={c.faqTitle}
        subtitle={c.faqSubtitle}
        items={c.faq.map((item) => ({ question: item.q, answer: item.a }))}
        footer={
          <Button size="lg" asChild>
            <a
              href="https://docs.open.cx/changelogs"
              target="_blank"
              rel="noopener noreferrer"
            >
              {c.faqCta}
              <ArrowUpRight className="size-4 ltr:ml-2 rtl:mr-2" />
            </a>
          </Button>
        }
      />

      <Testimonials />
    </>
  );
}
